import type { AppState } from "@/__main__/app-state.mjs";
import { readState } from "@/__main__/app-state.mjs";
import { RANK_SYMBOLS } from "@/game-lol/constants/constants.mjs";
import type { SummonerRank } from "@/game-lol/models/lol-summoner.mjs";
import { getDerivedIdFromSummoner } from "@/game-lol/utils/util.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

export default function useSummonerRanks(derivedId) {
  const state = useSnapshot(readState) as AppState;

  return getSummonerRanks(derivedId, state);
}

export function getSummonerRanks(
  derivedId = readState.volatile.currentSummoner ??
    getDerivedIdFromSummoner(
      readState.settings.loggedInAccounts.lol[
        readState.settings.lastLoggedInIdByGame.lol
      ],
    ),
  state = readState,
): SummonerRank[] {
  const ranks = state.lol.localSummonerRanks?.[derivedId];
  if (ranks?.length) return ranks;

  const root = state.lol.profiles[derivedId];
  if (root) {
    for (const rankKey of ["latestRanks", "ranks"])
      if (root[rankKey]?.length) return root[rankKey];
  }

  return [];
}

// safeguard against unusual rank tiers like in Arena
function getRankIndex(tier) {
  const index = Object.values(RANK_SYMBOLS).findIndex((k) => k === tier);
  return index !== -1 ? index : Number.MAX_SAFE_INTEGER;
}

export function getSummonerBestRank(
  derivedId = readState.volatile.currentSummoner ??
    getDerivedIdFromSummoner(
      readState.settings.loggedInAccounts.lol[
        readState.settings.lastLoggedInIdByGame.lol
      ],
    ),
  state = readState,
): SummonerRank | undefined {
  const ranks = getSummonerRanks(derivedId, state);

  // get the latest rank for each queue
  const latest_ranks = ranks.reduce((acc, cur) => {
    if (!acc[cur.queue]) {
      acc[cur.queue] = cur;
      return acc;
    }
    // check the date. only use the latest entry
    if (acc[cur.queue].insertedAt < cur.insertedAt) return acc;
    acc[cur.queue] = cur;
    const prevIdx = getRankIndex(acc[cur.queue].tier);
    const newIdx = getRankIndex(cur.tier);
    // lower is better rank
    if (prevIdx <= newIdx) return acc;

    return acc;
  }, {});
  // get the rank from the best queue
  const [max_rank] = Object.getOwnPropertySymbols(latest_ranks)
    .map((k) => latest_ranks[k])
    .sort((a, b) => getRankIndex(a.tier) - getRankIndex(b.tier));
  return max_rank;
}
